import {useCallback, useEffect, useMemo, useRef, useState} from '@wordpress/element';
import Lightbox from 'yet-another-react-lightbox';

import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import {motion} from "framer-motion";

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';

import {ImageLoader} from "./ImageLoader";
import {useSwipe} from "../../hooks/useSwipe";

export function ImageCarousel({images}) {
    const pics = useMemo(() => {
        const arr = Array.isArray(images) ? images : [];
        return arr.filter((i) => i?.original && i?.thumbnail);
    }, [images]);

    const [active, setActive] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!pics.length) return;
        setActive((i) => Math.max(0, Math.min(i, pics.length - 1)));
    }, [pics.length]);

    const slides = useMemo(() => {
        return pics.map((p) => ({
            src: p.original,
            thumbnail: p.thumbnail,
            width: p.originalWidth,
            height: p.originalHeight,
            title: p.alt,
        }));
    }, [pics]);

    const prev = useCallback(() => {
        setActive((i) => (i - 1 + pics.length) % pics.length);
    }, [pics.length]);

    const next = useCallback(() => {
        setActive((i) => (i + 1) % pics.length);
    }, [pics.length]);

    const show = useCallback((i) => {
        setActive(i);
        setOpen(true);
    }, []);

    const stripRef = useRef(null);

    // Vertical (desktop)
    const [canUp, setCanUp] = useState(false);
    const [canDown, setCanDown] = useState(false);

    // Horizontal (mobile)
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);
    const mainRef = useRef(null);
    const {x, handlePan, handlePanEnd} = useSwipe({
        onNext: next,
        onPrev: prev,
        containerRef: mainRef,
    });

    const updateStripState = useCallback(() => {
        const el = stripRef.current;
        if (!el) return;

        const maxY = el.scrollHeight - el.clientHeight;
        const top = el.scrollTop;
        setCanUp(top > 1);
        setCanDown(top < maxY - 1);

        const maxX = el.scrollWidth - el.clientWidth;
        const left = el.scrollLeft;
        setCanLeft(left > 1);
        setCanRight(left < maxX - 1);
    }, []);

    useEffect(() => {
        const el = stripRef.current;
        if (!el) return;

        updateStripState();

        const onScroll = () => updateStripState();
        el.addEventListener('scroll', onScroll, {passive: true});

        const ro = new ResizeObserver(() => updateStripState());
        ro.observe(el);

        return () => {
            el.removeEventListener('scroll', onScroll);
            ro.disconnect();
        };
    }, [updateStripState, pics.length]);

    const scrollStrip = useCallback((dir) => {
        const el = stripRef.current;
        if (!el) return;

        if (dir === 'up' || dir === 'down') {
            const page = Math.round(el.clientHeight * 0.8);
            el.scrollBy({top: dir === 'up' ? -page : page, behavior: 'smooth'});
        } else {
            const page = Math.round(el.clientWidth * 0.8);
            el.scrollBy({left: dir === 'left' ? -page : page, behavior: 'smooth'});
        }
    }, []);

    useEffect(() => {
        const el = stripRef.current;
        if (!el) return;

        const btn = el.querySelector(`[data-thumb-index="${active}"]`);
        if (!btn) return;

        const btnRect = btn.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        const isTopClipped = btnRect.top < elRect.top + 8;
        const isBottomClipped = btnRect.bottom > elRect.bottom - 8;
        const isLeftClipped = btnRect.left < elRect.left + 8;
        const isRightClipped = btnRect.right > elRect.right - 8;

        if (isTopClipped || isBottomClipped) {
            const targetTop = btn.offsetTop - Math.max(0, (el.clientHeight - btn.clientHeight) / 2);
            el.scrollTo({top: targetTop, behavior: 'smooth'});
        }

        if (isLeftClipped || isRightClipped) {
            const targetLeft = btn.offsetLeft - Math.max(0, (el.clientWidth - btn.clientWidth) / 2);
            el.scrollTo({left: targetLeft, behavior: 'smooth'});
        }
    }, [active]);

    if (!pics.length) return null;

    const THUMB_SIZE = 84;

    const btnBase = "absolute z-20 h-8 w-8 rounded-full bg-base-100/95 shadow flex items-center justify-center border border-base-300";

    return (
        <>
            <div className="w-full flex flex-col md:flex-row gap-4 h-[350px] sm:h-[450px] md:h-[600px]">
                {pics.length > 1 && (
                    <div className="relative w-full md:w-24 flex-none h-24 md:h-full overflow-hidden">
                        <div
                            className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white to-transparent z-10"/>
                        {/* Desktop: up/down arrows */}
                        {canUp && (
                            <button
                                type="button"
                                onClick={() => scrollStrip('up')}
                                className={`hidden md:flex ${btnBase} top-1 left-1/2 -translate-x-1/2`}
                                aria-label="Scroll thumbnails up"
                            >
                                ▴
                            </button>
                        )}
                        {canDown && (
                            <button
                                type="button"
                                onClick={() => scrollStrip('down')}
                                className={`hidden md:flex ${btnBase} bottom-1 left-1/2 -translate-x-1/2`}
                                aria-label="Scroll thumbnails down"
                            >
                                ▾
                            </button>
                        )}

                        {/* Mobile: left/right arrows */}
                        {canLeft && (
                            <button
                                type="button"
                                onClick={() => scrollStrip('left')}
                                className={`flex md:hidden ${btnBase} left-1 top-1/2 -translate-y-1/2`}
                                aria-label="Scroll thumbnails left"
                            >
                                ❮
                            </button>
                        )}
                        {canRight && (
                            <button
                                type="button"
                                onClick={() => scrollStrip('right')}
                                className={`flex md:hidden ${btnBase} right-1 top-1/2 -translate-y-1/2`}
                                aria-label="Scroll thumbnails right"
                            >
                                ❯
                            </button>
                        )}

                        <div
                            ref={stripRef}
                            className="
                                    h-full
                                    flex flex-row md:flex-col gap-2
                                    overflow-x-auto overflow-y-hidden md:overflow-x-hidden md:overflow-y-auto
                                    py-2 md:py-8
                                    snap-x md:snap-y snap-mandatory
                                    [-webkit-overflow-scrolling:touch]
                                    no-scrollbar
                                "
                        >
                            {pics.map((p, i) => {
                                const isActive = i === active;

                                return (
                                    <button
                                        key={p.thumbnail || i}
                                        type="button"
                                        data-thumb-index={i}
                                        onClick={() => setActive(i)}
                                        className={[
                                            'shrink-0 snap-start',
                                            'box-border',
                                            'p-0 m-0',
                                            'overflow-hidden',
                                            'rounded-xl border-2 bg-white',
                                            'flex items-stretch justify-stretch',
                                            'touch-manipulation',
                                            isActive ? 'border-primary' : 'border-base-300',
                                        ].join(' ')}
                                        style={{width: THUMB_SIZE, height: THUMB_SIZE}}
                                        aria-label={`Show image ${i + 1}`}
                                        aria-current={isActive ? 'true' : 'false'}
                                    >
                                        <ImageLoader
                                            src={p.thumbnail}
                                            alt={p.alt || ''}
                                            className="block w-full h-full object-contain"
                                            draggable={false}
                                            skeletonClassName="rounded-none"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div ref={mainRef} className="relative flex-1 min-h-0 overflow-hidden rounded-xl bg-white border border-base-300">
                    <motion.div
                        style={{
                            x,
                            display: "flex",
                            width: "300%",
                            marginLeft: "-100%",
                            height: "100%",
                            touchAction: "pan-y",
                        }}
                        onPan={handlePan}
                        onPanEnd={handlePanEnd}
                    >
                        {[-1, 0, 1].map(offset => {
                            const i = (active + offset + pics.length) % pics.length;
                            const p = pics[i];
                            return (
                                <div key={i} style={{width: "33.333%", flexShrink: 0}} className="flex items-center justify-center bg-white">
                                    <button
                                        type="button"
                                        onClick={() => offset === 0 && show(active)}
                                        className="w-full h-full flex items-center justify-center p-0 m-0"
                                    >
                                        <ImageLoader
                                            src={p.original}
                                            alt={p.alt || ''}
                                            className="block h-full w-full max-w-full max-h-full object-contain"
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </motion.div>

                    {pics.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={prev}
                                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-base-100/95 shadow flex items-center justify-center z-10 border border-base-300"
                                aria-label="Previous image"
                            >
                                ❮
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-base-100/95 shadow flex items-center justify-center z-10 border border-base-300"
                                aria-label="Next image"
                            >
                                ❯
                            </button>
                        </>
                    )}

                    {pics.length > 1 && (
                        <div className="absolute bottom-2 right-2 z-10 px-2 py-1 rounded-full bg-base-100/95 border border-base-300 text-xs shadow">
                            {active + 1} / {pics.length}
                        </div>
                    )}
                </div>
            </div>

            <Lightbox
                open={open}
                close={() => setOpen(false)}
                index={active}
                slides={slides}
                plugins={[Thumbnails, Zoom, Counter]}
                carousel={{finite: true}}
                thumbnails={{preload: 0, width: 80, height: 60, gap: 12, vignette: false}}
                zoom={{maxZoomPixelRatio: 4}}
            />
        </>
    );
}